import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Checkbox,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import GlobalStyles from '../../../../styles/GlobalStyles.web';
import { ConflictChange } from '../../lib/orderPickingConstants';
import {
  incrementPickedQtyByCase,
  decrementPickedQtyByCase,
  onReceiveQtyTextChange,
  applyPickAll,
} from '../../lib/orderPickingItems';

const touchMinMobile = 44;

interface Props {
  item: any;
  index: number;
  onItemChange: (item: any) => void;
}

const OrderDetailLineCard = (props: Props) => {
  const { item: initial, index, onItemChange } = props;
  const [item, setItem] = useState(initial);
  const [pickQtyOpen, setPickQtyOpen] = useState(false);
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const controlMin = isSmDown ? touchMinMobile : 32;

  useEffect(() => {
    setItem(initial);
  }, [initial]);

  const editOItem = (next: any) => {
    setItem(next);
    onItemChange(next);
  };

  const originalQty = item.original_ordered_quantity;

  const openPickModal = () => setPickQtyOpen(true);
  const closePickModal = () => setPickQtyOpen(false);

  const renderPickQtyModal = () => (
    <Dialog open={pickQtyOpen} onClose={closePickModal} fullWidth maxWidth="sm">
      <DialogTitle>Manage pick quantities</DialogTitle>
      <DialogContent>
        <Typography gutterBottom color="primary">
          {item.product?.name}
        </Typography>
        <Typography variant="body2" gutterBottom>
          Total picked: {item.pickedQty}
        </Typography>
        {item.codePickedQtys?.map((cpq: any, cpqIndex: number) => (
          <Card key={cpqIndex} variant="outlined" style={{ marginBottom: 8 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" flexWrap="wrap">
                <Typography variant="body2">
                  {cpqIndex + 1}. {cpq.product_code}
                </Typography>
                <Typography variant="body2">Case qty — {cpq.case_quantity}</Typography>
              </Box>
              <Box display="flex" alignItems="center" flexWrap="wrap" mt={1}>
                <Typography variant="body2" style={{ minWidth: 80 }}>
                  Picked qty
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  style={{ width: 88 }}
                  disabled={item.confirmed || item.is_deleted}
                  value={cpq.pickedQty}
                  onChange={(e) => {
                    const v = e.target.value;
                    onReceiveQtyTextChange(v, item, cpq, cpqIndex);
                    editOItem({ ...item });
                  }}
                />
                {!item.is_deleted && (
                  <Box display="flex" ml={1}>
                    <IconButton
                      size="small"
                      style={{ minWidth: controlMin, minHeight: controlMin }}
                      disabled={item.confirmed || item.is_deleted}
                      onClick={() => {
                        decrementPickedQtyByCase(1, item, cpq, cpqIndex, originalQty);
                        editOItem({ ...item });
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                      <Typography variant="caption">1</Typography>
                    </IconButton>
                    <IconButton
                      size="small"
                      style={{ minWidth: controlMin, minHeight: controlMin }}
                      disabled={item.confirmed || item.is_deleted}
                      onClick={() => {
                        incrementPickedQtyByCase(1, item, cpq, cpqIndex, item.quantity);
                        editOItem({ ...item });
                      }}
                    >
                      <AddIcon fontSize="small" />
                      <Typography variant="caption">1</Typography>
                    </IconButton>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={closePickModal} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  const borderColor = () => {
    const pq = Number(item.pickedQty);
    const q = Number(item.quantity);
    if (pq > q) {
      return GlobalStyles.secondary;
    }
    if (pq === q) {
      return GlobalStyles.green;
    }
    return GlobalStyles.red;
  };

  const renderQtyInfo = () =>
    item.has_changed &&
    (item.change_reason === ConflictChange.EXPECTED_QTY_CHANGE ||
      item.change_reason === ConflictChange.ITEM_DELETED) ? (
      <Typography variant="body2" noWrap={!isSmDown}>
        Old qty — <span style={{ textDecoration: 'line-through', color: 'red' }}>{item.old_value}</span>
        {'  '}
        New qty — <span style={{ color: 'green' }}>{item.new_value}</span>
      </Typography>
    ) : (
      <Typography variant="body2" noWrap={!isSmDown}>
        Ordered qty — {item.quantity}
      </Typography>
    );

  return (
    <Card
      style={{
        marginBottom: 8,
        borderLeft: `6px solid ${
          item.confirmed ? borderColor() : item.pickedQty === '' ? '#fff' : borderColor()
        }`,
      }}
    >
      <CardContent style={{ padding: isSmDown ? 12 : 8, paddingBottom: isSmDown ? 12 : 8 }}>
        {!isSmDown ? (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography
              variant="body2"
              noWrap
              style={{
                flex: 1,
                minWidth: 0,
                textDecoration: item.is_deleted ? 'line-through' : undefined,
                color: item.is_deleted ? 'red' : item.is_new ? 'green' : undefined,
              }}
            >
              {index + 1}. {item.product?.name}
            </Typography>
            <Box display="flex" alignItems="center">
              {renderQtyInfo()}
              {!item.is_deleted && (
                <Typography variant="body2" style={{ marginLeft: 10 }} noWrap>
                  Picked {item.pickedQty}
                </Typography>
              )}
            </Box>
            {!item.is_deleted && item.codePickedQtys?.length === 1 && (
              <Box display="flex" alignItems="center">
                <IconButton
                  size="small"
                  style={{ minWidth: controlMin, minHeight: controlMin }}
                  disabled={item.confirmed || item.is_deleted}
                  onClick={() => {
                    decrementPickedQtyByCase(1, item, item.codePickedQtys[0], 0, originalQty);
                    editOItem({ ...item });
                  }}
                >
                  <RemoveIcon />
                  <Typography variant="caption">1</Typography>
                </IconButton>
                <IconButton
                  size="small"
                  style={{ minWidth: controlMin, minHeight: controlMin }}
                  disabled={item.confirmed || item.is_deleted}
                  onClick={() => {
                    incrementPickedQtyByCase(1, item, item.codePickedQtys[0], 0, item.quantity);
                    editOItem({ ...item });
                  }}
                >
                  <AddIcon />
                  <Typography variant="caption">1</Typography>
                </IconButton>
              </Box>
            )}
            {!item.is_deleted && item.codePickedQtys?.length > 1 && (
              <Button size="small" variant="outlined" color="primary" onClick={openPickModal}>
                Pick qtys
              </Button>
            )}
            {!item.is_deleted && item.codePickedQtys?.length === 1 && (
              <Box display="flex" alignItems="center" mr={0.5}>
                <Checkbox
                  style={{ minWidth: controlMin, minHeight: controlMin, padding: 4 }}
                  disabled={item.confirmed}
                  checked={item.pickedAll}
                  onChange={() => {
                    applyPickAll(item, !item.pickedAll);
                    editOItem({ ...item });
                  }}
                  color="primary"
                />
                <Typography variant="body2">Pick all</Typography>
              </Box>
            )}
            {!item.is_deleted && (
              <Box display="flex" alignItems="center">
                <Checkbox
                  style={{ minWidth: controlMin, minHeight: controlMin, padding: 4 }}
                  checked={item.confirmed}
                  onChange={() => {
                    item.confirmed = !item.confirmed;
                    item.disabled = item.confirmed;
                    editOItem({ ...item });
                  }}
                  color="primary"
                />
                <Typography variant="body2">Done</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography
                variant="body2"
                style={{
                  flex: 1,
                  minWidth: 0,
                  marginRight: 8,
                  wordBreak: 'break-word',
                  lineHeight: 1.3,
                  textDecoration: item.is_deleted ? 'line-through' : undefined,
                  color: item.is_deleted ? 'red' : item.is_new ? 'green' : undefined,
                }}
              >
                {index + 1}. {item.product?.name}
              </Typography>
              <Box display="flex" alignItems="center">
                {!item.is_deleted && item.codePickedQtys?.length === 1 && (
                  <Box display="flex" alignItems="center" mr={0.5}>
                    <Checkbox
                      style={{ minWidth: controlMin, minHeight: controlMin, padding: 4 }}
                      disabled={item.confirmed}
                      checked={item.pickedAll}
                      onChange={() => {
                        applyPickAll(item, !item.pickedAll);
                        editOItem({ ...item });
                      }}
                      color="primary"
                    />
                    <Typography variant="body2">Pick all</Typography>
                  </Box>
                )}
                {!item.is_deleted && (
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      style={{ minWidth: controlMin, minHeight: controlMin, padding: 4 }}
                      checked={item.confirmed}
                      onChange={() => {
                        item.confirmed = !item.confirmed;
                        item.disabled = item.confirmed;
                        editOItem({ ...item });
                      }}
                      color="primary"
                    />
                    <Typography variant="body2">Done</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              mt={0.5}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ minWidth: 0 }}>
                {renderQtyInfo()}
                {!item.is_deleted && (
                  <Typography variant="body2" style={{ marginLeft: 10 }}>
                    Picked {item.pickedQty}
                  </Typography>
                )}
              </Box>

              {!item.is_deleted && (
                <Box display="flex" alignItems="center">
                  {item.codePickedQtys?.length > 1 && (
                    <Button size="small" variant="outlined" color="primary" onClick={openPickModal}>
                      Pick qtys
                    </Button>
                  )}
                  {item.codePickedQtys?.length === 1 && (
                    <Box display="flex" alignItems="center">
                      <IconButton
                        size="small"
                        style={{ minWidth: controlMin, minHeight: controlMin }}
                        disabled={item.confirmed || item.is_deleted}
                        onClick={() => {
                          decrementPickedQtyByCase(
                            1,
                            item,
                            item.codePickedQtys[0],
                            0,
                            originalQty,
                          );
                          editOItem({ ...item });
                        }}
                      >
                        <RemoveIcon />
                        <Typography variant="caption">1</Typography>
                      </IconButton>
                      <IconButton
                        size="small"
                        style={{ minWidth: controlMin, minHeight: controlMin }}
                        disabled={item.confirmed || item.is_deleted}
                        onClick={() => {
                          incrementPickedQtyByCase(
                            1,
                            item,
                            item.codePickedQtys[0],
                            0,
                            item.quantity,
                          );
                          editOItem({ ...item });
                        }}
                      >
                        <AddIcon />
                        <Typography variant="caption">1</Typography>
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
        {isSmDown && !item.is_deleted && item.codePickedQtys?.length > 1 && (
          <Box mt={0.25}>
            <Typography variant="caption" color="textSecondary" noWrap={!isSmDown}>
              {item.codePickedQtys
                .map((cpq: any) => `${cpq.product_code}: ${cpq.pickedQty || 0}`)
                .join(' | ')}
            </Typography>
          </Box>
        )}
        {renderPickQtyModal()}
      </CardContent>
    </Card>
  );
};

export default OrderDetailLineCard;
